import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from './Layout';
import StatusInfo from '../components/StatusInfo';
import ListPager from '../components/ListPager';
import { fmtMoney } from '../utils/format';

const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
const LIMIT = 50;

// CL-001, CL-012, CL-123
function formatClinicId(id) {
  return 'CL-' + String(id).padStart(3, '0');
}

export default function Clinics() {
  const [clinics, setClinics] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [filterId, setFilterId] = useState('');
  const [filterName, setFilterName] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', api_endpoint: '', api_key: '' });
  const [addErr, setAddErr] = useState('');
  const [adding, setAdding] = useState(false);
  const navigate = useNavigate();

  const key = localStorage.getItem('adminKey');
  const headers = { 'X-Admin-Key': key };

  const createClinic = async () => {
    if (!form.name.trim()) { setAddErr('Введите название'); return; }
    setAdding(true); setAddErr('');
    try {
      const r = await axios.post(`${API}/admin/clinics`, form, { headers });
      navigate(`/clinics/${r.data.id}`);
    } catch (e) {
      setAddErr(e?.response?.data?.detail || 'Не удалось создать клинику');
      setAdding(false);
    }
  };

  const fetchData = useCallback(() => {
    const params = {
      search: filterName || undefined,
      id_search: filterId || undefined,
      sort: sortField, order: sortDir,
      limit: LIMIT, offset,
    };
    axios.get(`${API}/admin/clinics`, { headers: { 'X-Admin-Key': key }, params })
      .then(r => { setClinics(r.data.items); setTotal(r.data.total); });
  }, [filterName, filterId, sortField, sortDir, offset, key]);

  useEffect(() => {
    const t = setTimeout(() => fetchData(), 250);
    return () => clearTimeout(t);
  }, [fetchData]);

  const toggleSort = (field) => {
    setOffset(0);
    if (sortField === field) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const arrow = (field) => {
    if (sortField !== field) return '';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  };

  const onFilter = (setter) => (e) => { setter(e.target.value); setOffset(0); };

  return (
    <Layout>
      <div style={styles.pageHead}>
        <h1 style={styles.title}>
          Клиники ({total})
        </h1>
        <button style={styles.addBtn} onClick={() => { setForm({ name: '', api_endpoint: '', api_key: '' }); setAddErr(''); setShowAdd(true); }}>
          + Добавить клинику
        </button>
      </div>

      {showAdd && (
        <div style={styles.modalOverlay} onClick={() => setShowAdd(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Новая клиника</h3>
            <label style={styles.label}>Название *</label>
            <input style={styles.modalInput} value={form.name} autoFocus
              onChange={e => setForm({ ...form, name: e.target.value })} />
            <label style={styles.label}>API endpoint</label>
            <input style={styles.modalInput} placeholder="https://mis.clinic.ru/refero" value={form.api_endpoint}
              onChange={e => setForm({ ...form, api_endpoint: e.target.value })} />
            <label style={styles.label}>API key</label>
            <input style={styles.modalInput} value={form.api_key}
              onChange={e => setForm({ ...form, api_key: e.target.value })} />
            {addErr && <div style={styles.errText}>{addErr}</div>}
            <div style={styles.modalActions}>
              <button style={styles.btnGhost} onClick={() => setShowAdd(false)}>Отмена</button>
              <button style={styles.addBtn} onClick={createClinic} disabled={adding}>
                {adding ? 'Создаю…' : 'Создать'}
              </button>
            </div>
            <div style={styles.hintText}>Условия (% и квоту) зададите на странице клиники после создания.</div>
          </div>
        </div>
      )}

      <div style={styles.table}>
        <div style={styles.thead}>
          <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => toggleSort('id')}>
            ID{arrow('id')}
          </div>
          <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => toggleSort('name')}>
            Клиника{arrow('name')}
          </div>
          <div style={styles.th}>Сеть</div>
          <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => toggleSort('referrals_count')}>
            Направлений{arrow('referrals_count')}
          </div>
          <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => toggleSort('transactions_count')}>
            Транзакций{arrow('transactions_count')}
          </div>
          <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => toggleSort('total_commissions')}>
            Комиссий{arrow('total_commissions')}
          </div>
          <div style={styles.th}>Статус<StatusInfo def="clinic_active" /></div>
          <div style={styles.th}>API<StatusInfo def="api" /></div>
          <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => toggleSort('created_at')}>
            Подключена{arrow('created_at')}
          </div>
        </div>

        {/* Строка фильтров */}
        <div style={styles.filterRow}>
          <div style={styles.filterCell}>
            <input
              style={styles.filterInput}
              placeholder="CL-001"
              value={filterId}
              onChange={onFilter(setFilterId)}
            />
          </div>
          <div style={styles.filterCell}>
            <input
              style={styles.filterInput}
              placeholder="Название"
              value={filterName}
              onChange={onFilter(setFilterName)}
            />
          </div>
          <div style={styles.filterCell}></div>
          <div style={styles.filterCell}></div>
          <div style={styles.filterCell}></div>
          <div style={styles.filterCell}></div>
          <div style={styles.filterCell}></div>
          <div style={styles.filterCell}></div>
          <div style={styles.filterCell}></div>
        </div>

        {clinics.map(c => (
          <div key={c.id} style={styles.row}>
            <div style={styles.td}>
              <span style={styles.clinicId} onClick={() => navigate(`/clinics/${c.id}`)}>{formatClinicId(c.id)}</span>
            </div>
            <div style={styles.td}>
              <div style={styles.nameLink} onClick={() => navigate(`/clinics/${c.id}`)}>{c.name}</div>
            </div>
            <div style={styles.td}>
              {c.network_id
                ? <span style={styles.nameLink} onClick={() => navigate(`/networks/${c.network_id}`)}>{c.network_name}</span>
                : <span style={{ color: '#64748b' }}>—</span>}
            </div>
            <div style={styles.td}>{c.network_id ? <span style={styles.inNetwork}>В сети</span> : c.referrals_count}</div>
            <div style={styles.td}>{c.network_id ? <span style={styles.inNetwork}>В сети</span> : c.transactions_count}</div>
            <div style={{ ...styles.td, color: '#2EC4B6', fontWeight: 600 }}>
              {c.network_id ? <span style={styles.inNetwork}>В сети</span> : `${fmtMoney(c.total_commissions)} ₽`}
            </div>
            <div style={styles.td}>
              {!c.is_active
                ? <span style={styles.badgeRed}>Отключена</span>
                : (!c.network_id && c.is_hidden)
                ? <span style={styles.badgeOrange}>Скрыто</span>
                : (c.network_id && c.network_is_hidden)
                ? <span style={styles.badgeOrange}>Скрыто (сеть)</span>
                : <span style={styles.badgeGreen}>Активна</span>}
            </div>
            <div style={styles.td}>
              {c.network_id && !c.has_own_api
                ? <span style={{ color: '#64748b' }}>—</span>
                : c.api_status === 'active' ? <span style={styles.badgeGreen}>активно</span>
                : c.api_status === 'failed' ? <span style={styles.badgeRed}>ошибка</span>
                : <span style={styles.badgeGray}>не пров.</span>}
            </div>
            <div style={{ ...styles.td, color: '#64748b', fontSize: 12 }}>
              {new Date(c.created_at).toLocaleDateString('ru')}
            </div>
          </div>
        ))}

        {clinics.length === 0 && (
          <div style={styles.empty}>Ничего не найдено</div>
        )}
      </div>

      <ListPager total={total} limit={LIMIT} offset={offset} onPage={setOffset} />
    </Layout>
  );
}

const styles = {
  pageHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
  title: { color: 'white', fontSize: 24, fontWeight: 700, margin: 0 },
  addBtn: { background: '#2EC4B6', color: '#0f172a', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 },
  modal: { background: '#1e293b', borderRadius: 12, padding: 24, width: 440, maxWidth: '90vw', border: '1px solid #334155' },
  modalTitle: { color: 'white', fontSize: 18, fontWeight: 700, margin: '0 0 16px' },
  label: { display: 'block', color: '#64748b', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, margin: '10px 0 4px' },
  modalInput: { width: '100%', boxSizing: 'border-box', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 6, padding: '9px 12px', fontSize: 14, outline: 'none' },
  errText: { color: '#f87171', fontSize: 13, marginTop: 12 },
  hintText: { color: '#64748b', fontSize: 12, marginTop: 14 },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 },
  btnGhost: { background: 'transparent', color: '#94a3b8', border: '1px solid #334155', borderRadius: 8, padding: '9px 18px', fontSize: 14, cursor: 'pointer' },
  table: { background: '#1e293b', borderRadius: 12, overflow: 'hidden' },
  thead: { display: 'grid', gridTemplateColumns: '0.8fr 2fr 1.3fr 1fr 1fr 1.4fr 1fr 1fr 1fr', padding: '12px 20px', borderBottom: '1px solid #334155' },
  th: { fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 },
  thClickable: { cursor: 'pointer', userSelect: 'none' },
  filterRow: { display: 'grid', gridTemplateColumns: '0.8fr 2fr 1.3fr 1fr 1fr 1.4fr 1fr 1fr 1fr', padding: '8px 20px', borderBottom: '1px solid #334155', background: '#172033', gap: 8 },
  filterCell: { display: 'flex', alignItems: 'center' },
  filterInput: {
    width: '100%', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155',
    borderRadius: 6, padding: '5px 8px', fontSize: 12, outline: 'none', boxSizing: 'border-box',
  },
  row: { display: 'grid', gridTemplateColumns: '0.8fr 2fr 1.3fr 1fr 1fr 1.4fr 1fr 1fr 1fr', padding: '16px 20px', borderBottom: '1px solid #1e293b', background: '#0f172a', margin: '1px 0' },
  td: { fontSize: 14, color: '#e2e8f0', display: 'flex', flexDirection: 'column', justifyContent: 'center' },
  clinicId: { fontFamily: 'monospace', fontSize: 13, color: '#94a3b8', background: '#1e293b', padding: '2px 8px', borderRadius: 6, display: 'inline-block', width: 'fit-content', border: '1px solid #334155', cursor: 'pointer' },
  name: { fontWeight: 600 },
  nameLink: { fontWeight: 600, color: '#2EC4B6', cursor: 'pointer' },
  inNetwork: { color: '#64748b', fontSize: 12, fontStyle: 'italic', fontWeight: 400 },
  empty: { color: '#64748b', fontSize: 14, textAlign: 'center', padding: '40px 0' },
  badgeGreen: { background: '#052e16', color: '#4ade80', padding: '3px 8px', borderRadius: 20, fontSize: 12, display: 'inline-block' },
  badgeRed: { background: '#450a0a', color: '#f87171', padding: '3px 8px', borderRadius: 20, fontSize: 12, display: 'inline-block' },
  badgeGray: { background: '#1e293b', color: '#64748b', padding: '3px 8px', borderRadius: 20, fontSize: 12, display: 'inline-block', border: '1px solid #334155' },
  badgeOrange: { background: '#451a03', color: '#fb923c', padding: '3px 8px', borderRadius: 20, fontSize: 12, display: 'inline-block' },
};